import React, { createContext, useMemo, useContext, useState, useCallback, useEffect } from 'react'
import { DepositMethodType, FinanceDepositFeeMethodQuery, FinanceDepositWayGroupInfoArrays, FinanceDepositWayGroupSearchQuery, GDKError, WithdrawPointType } from '@golden/gdk-admin'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import OnOffCheckbox, { PropTypes as OnOffCheckboxProps } from '../../default/form/OnOffCheckbox'
import NumberInput from '../../default/form/NumberInput'
import FormStateProvider from '../../default/form/FormStateProvider'
import FormField from '../../default/form/FormField'
import FormSubmitButton from '../../default/form/FormSubmitButton'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { FormPropType, getValueFromChangeEvent, getValueFromValue, getValueFromCheckboxEvent, getCashInputProps } from '../../../utils/default/FormHelper'
import { createValidateNotEmpty, createValidateRange } from '../../../utils/default/Validator'
import { createDefaultFormState, ValueGetter, FormValidation, ChangedFormGetter } from '../../../utils/default/FormHook'
import { InitialFormFunc, useGetData, useGetDataByPayload } from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import CoreTable from '../../default/present/CoreTable'
import { createTableData } from '../../../utils/default/TableHelper'
import { Checkbox } from '@material-ui/core'
export interface EditFinanceDepositWayGroupFormType {
  name: string
  isActive: boolean
  cashMin: number | null
  cashMax: number | null
  method?: DepositMethodType | '--'
  supportAccounts: number[]
  supportWays: number[]
}
interface RowType {
  id: number
  name: string
  account: string
  forwarder: string
  min: string
  max: string
  openLayers: string
  isActive: string
  isSelected: React.ReactElement
}

export const initialForm: InitialFormFunc<EditFinanceDepositWayGroupFormType> = (defaultForm) => ({
  name: '',
  isActive: false,
  cashMin: null,
  cashMax: null,
  method: '--',
  supportAccounts: [],
  supportWays: [],
  ...defaultForm
})

const getValueFromEvent: ValueGetter<EditFinanceDepositWayGroupFormType> = {
  name: getValueFromChangeEvent,
  isActive: getValueFromCheckboxEvent,
  cashMin: getValueFromChangeEvent,
  cashMax: getValueFromChangeEvent,
  method: getValueFromChangeEvent,
  supportAccounts: getValueFromValue,
  supportWays: getValueFromValue
}

interface PropTypes extends FormPropType<EditFinanceDepositWayGroupFormType> {
  title: string
  okText: string
  onBack: () => void
  searchContext: React.Context<[FinanceDepositWayGroupSearchQuery, React.Dispatch<React.SetStateAction<FinanceDepositWayGroupSearchQuery>>]>
  groupId?: number
  pageFlow?: ReturnType<typeof usePageFlow>
}

interface searchProps {
  context: React.Context<[FinanceDepositWayGroupSearchQuery, React.Dispatch<React.SetStateAction<FinanceDepositWayGroupSearchQuery>>]>
  groupId?: number
  pageFlow?: ReturnType<typeof usePageFlow>
}

const FormContext = createContext(createDefaultFormState(initialForm()))
const TextField = React.memo(MuiTextField)
const cashInputProps = getCashInputProps()
const specialCharactersRegex = /[~!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/～！＠＃＄％︿＆＊（）＿＋－＝｛｝［］｜＼：；＂＇＜＞，．？／]/

const SearchMethodsForm: React.FC<searchProps> = (props) => {
  const { context, groupId, pageFlow } = props
  const gdk = useGDK()
  const classes = useCommonStyles()
  const { t } = useT()
  const { value: form } = useContext(FormContext)
  const [_, setSearchState] = useContext(context)
  const [methodList, setMethodList] = useState<FinanceDepositFeeMethodQuery[]>([])

  const methodOptions = useMemo(() => [
    { name: t('common.pleaseChoose'), value: '--' },
    ...methodList.filter((el) => (el.method !== WithdrawPointType.AGENT_TRANSFER_DEPOSIT)).map((el) => ({ name: el.name, value: el.method }))
  ], [methodList, t])

  useGetData({
    gdkFunc: () => gdk.finance.getDepositFeeMethod(),
    gdkFuncDependencies: [gdk],
    onSuccess: (res) => {
      setMethodList(res)
    }
  })

  const disableSearch = useMemo(() => {
    return Boolean(form.method === '--' || !form.cashMin || !form.cashMax)
  }, [form])
  const handleSearch = useCallback(() => {
    if (form.method === '--') return
    setSearchState({
      method: form.method,
      cash_min: Number(form.cashMin ?? 0),
      cash_max: Number(form.cashMax ?? 0),
      group_id: groupId ?? undefined
    })
  }, [form, setSearchState])

  useEffect(() => {
    if (!!groupId && pageFlow?.status.showContent) {
      handleSearch()
    }
  }, [pageFlow?.status.showContent, groupId])

  return (
    <>
      <Grid container direction="row" wrap='wrap' spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography className={classes.bold}>{t('common.depositWay')}</Typography>
          <FormField<EditFinanceDepositWayGroupFormType, DropDownProps>
            component={DropDown}
            context={FormContext}
            name="method"
            options={methodOptions}
            disabled={!!groupId}
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={5}>
          <FormField<EditFinanceDepositWayGroupFormType, TextFieldProps>
            context={FormContext}
            component={NumberInput}
            name="cashMin"
            label={t('common.minDepositMoney')}
            inputProps={cashInputProps}
            fullWidth
          />
        </Grid>
        <Grid item xs={5}>
          <FormField<EditFinanceDepositWayGroupFormType, TextFieldProps>
            context={FormContext}
            component={NumberInput}
            name="cashMax"
            label={t('common.maxDepositMoney')}
            inputProps={cashInputProps}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <Button
            onClick={handleSearch}
            className={classes.purpleGradualButton}
            disabled={disableSearch}
          >
            {t('common.search')}
          </Button>
        </Grid>
      </Grid>
    </>
  )
}

const SearchResTable: React.FC<searchProps> = (props) => {
  const { context } = props
  const classes = useCommonStyles()
  const tableClasses = useMemo(() => ({
    head: classes.greyTableHead,
    row: classes.tableRow,
    cellHead: classes.tableCellHead,
    cellBody: classes.nowrap
  }), [classes])

  const { t } = useT()
  const gdk = useGDK()
  const pageFlow = usePageFlow()

  const { value: form, dispatch } = useContext(FormContext)
  const [searchState, setSearchState] = useContext(context)
  const [searchRes, setSearchRes] = useState<FinanceDepositWayGroupInfoArrays>({ accounts: [], ways: [] })

  useGetDataByPayload({
    payload: searchState,
    gdkFunc: (payload) => gdk.finance.getDepositWayGroupSearch(payload),
    gdkFuncDependencies: [gdk, searchState],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res) => {
      setSearchRes(res)
      dispatch({
        type: 'change',
        label: 'supportWays',
        value: res.ways.filter((el) => (el.is_selected)).map((el) => (el.id))
      })
      dispatch({
        type: 'change',
        label: 'supportAccounts',
        value: res.accounts.filter((el) => (el.is_selected)).map((el) => (el.id))
      })
      pageFlow.setContentShow()
    },
    onError: (error: GDKError) => {
      pageFlow.setGDKError(error)
    },
    canLoadData: !!searchState.method
  })

  const accountRows = useMemo(() => {
    return searchRes.accounts.map((item) => {
      return {
        id: item.id,
        name: item.name,
        account: item.account,
        forwarder: '',
        min: item.cash_min,
        max: item.cash_max,
        openLayers: item.open_layers,
        isActive: item.is_active ? t('common.toggleOn') : t('common.toggleOff'),
        isSelected: (
          <Checkbox
            checked={form.supportAccounts.includes(item.id)}
            onChange={(event) => {
              const newVal = event.target.checked ? [...form.supportAccounts, item.id] : form.supportAccounts.filter((el) => (el !== item.id))
              dispatch({
                type: 'change',
                label: 'supportAccounts',
                value: newVal
              })
            }}
          />
        )
      } as RowType
    })
  }, [searchRes.accounts, form])

  const wayRows = useMemo(() => {
    return searchRes.ways.map((item) => {
      return {
        id: item.id,
        name: item.name,
        account: '',
        forwarder: item.forwarder,
        min: item.cash_min,
        max: item.cash_max,
        openLayers: item.open_layers,
        isActive: item.is_active ? t('common.toggleOn') : t('common.toggleOff'),
        isSelected: (
          <Checkbox
            checked={form.supportWays.includes(item.id)}
            onChange={(event) => {
              const newVal = event.target.checked ? [...form.supportWays, item.id] : form.supportWays.filter((el) => (el !== item.id))
              dispatch({
                type: 'change',
                label: 'supportWays',
                value: newVal
              })
            }}
          />
        )
      } as RowType
    })
  }, [searchRes.accounts, form])

  const accountsData = useMemo(() => {
    return createTableData(
      {
        id: {
          label: '',
          value: 'id'
        },
        name: {
          label: t('common.name'),
          value: 'name',
          align: 'center'
        },
        account: {
          label: t('common.account'),
          value: 'account',
          align: 'center'
        },
        forwarder: {
          label: '',
          value: 'forwarder'
        },
        min: {
          label: t('common.minDepositMoney'),
          value: 'min',
          align: 'center'
        },
        max: {
          label: t('common.maxDepositMoney'),
          value: 'max',
          align: 'center'
        },
        openLayers: {
          label: t('common.openPlayerLayer'),
          value: 'openLayers',
          align: 'center'
        },
        isActive: {
          label: t('common.status'),
          value: 'isActive',
          align: 'center'
        },
        isSelected: {
          label: t('common.choose'),
          value: 'isSelected',
          align: 'center'
        }
      },
      [
        'name',
        'account',
        'min',
        'max',
        'openLayers',
        'isActive',
        'isSelected'
      ],
      accountRows,
      'id'
    )
  }, [t, accountRows])

  const waysData = useMemo(() => {
    return createTableData(
      {
        id: {
          label: '',
          value: 'id'
        },
        name: {
          label: t('common.courierName'),
          value: 'name',
          align: 'center'
        },
        forwarder: {
          label: t('common.forwarder'),
          value: 'forwarder',
          align: 'center'
        },
        account: {
          label: '',
          value: 'account'
        },
        min: {
          label: t('common.minDepositMoney'),
          value: 'min',
          align: 'center'
        },
        max: {
          label: t('common.maxDepositMoney'),
          value: 'max',
          align: 'center'
        },
        openLayers: {
          label: t('common.openPlayerLayer'),
          value: 'openLayers',
          align: 'center'
        },
        isActive: {
          label: t('common.status'),
          value: 'isActive',
          align: 'center'
        },
        isSelected: {
          label: t('common.choose'),
          value: 'isSelected',
          align: 'center'
        }
      },
      [
        'forwarder',
        'name',
        'min',
        'max',
        'openLayers',
        'isActive',
        'isSelected'
      ],
      wayRows,
      'id'
    )
  }, [t, wayRows])

  const showTable = useMemo(() => {
    return (!!searchState.method)
  }, [searchState])

  useEffect(() => {
    if (!showTable) return
    dispatch({
      type: 'change',
      label: 'supportAccounts',
      value: []
    })
    dispatch({
      type: 'change',
      label: 'supportWays',
      value: []
    })
    setSearchState({ method: undefined })
  }, [form.method])

  return (
    <Box padding={1}>
      {showTable && (
        <LoadingAndErrorFrame {...pageFlow.status}>
          {Boolean(searchRes.accounts.length || (!searchRes.accounts.length && !searchRes.ways.length)) && <Box padding={2}>
            <CoreTable
              classes={tableClasses}
              data={accountsData}
              total={searchRes.accounts.length}
            />
          </Box>}
          {Boolean(searchRes.ways.length) && <Box padding={2}>
            <CoreTable
              classes={tableClasses}
              data={waysData}
              total={searchRes.ways.length}
            />
          </Box>}
        </LoadingAndErrorFrame>
      )}
    </Box>
  )
}

const EditFinanceDepositWayGroupForm: React.FC<PropTypes> = (props) => {
  const { title, okText, onBack, onSubmit, defaultValue, groupId, searchContext, pageFlow } = props
  const theme = useTheme()
  const classes = useCommonStyles()
  const { t } = useT()

  const validation = useMemo(() => {
    return {
      name: [
        {
          func: createValidateRange('name', (value: unknown) => (value as string).length, 'lessEqual', 6, t('error.mustLessThanWords', { number: 6 })),
          when: ['change', 'beforeClickSubmit']
        },
        {
          func: createValidateNotEmpty('name', t),
          when: ['change', 'beforeClickSubmit']
        }
      ],
      isActive: [],
      cashMin: [
        {
          func: createValidateNotEmpty('cashMin', t),
          when: ['change', 'beforeClickSubmit']
        }
      ],
      cashMax: [
        {
          func: createValidateNotEmpty('cashMax', t),
          when: ['change', 'beforeClickSubmit']
        }
      ],
      method: [],
      supportAccounts: [
        {
          func: (value: number[], form) => {
            if (value.length + form.supportWays.length === 0) {
              return { isPass: false, stop: true, newError: { supportAccounts: null } }
            }
            return { isPass: true, stop: false, newError: {} }
          },
          when: ['change', 'beforeClickSubmit']
        }
      ],
      supportWays: [
        {
          func: (value: number[], form) => {
            if (value.length + form.supportAccounts.length === 0) {
              return { isPass: false, stop: true, newError: { supportAccounts: null } }
            }
            return { isPass: true, stop: false, newError: {} }
          },
          when: ['change', 'beforeClickSubmit']
        }
      ]
    } as FormValidation<EditFinanceDepositWayGroupFormType>
  }, [t])

  const getChangedForm: ChangedFormGetter<EditFinanceDepositWayGroupFormType> = {
    name: (value, form) => {
      return {
        ...form,
        name: value.replace(specialCharactersRegex, '')
      }
    },
    method: (value, form) => {
      return {
        ...form,
        method: value
      }
    }
  }

  return (
    <FormStateProvider
      context={FormContext}
      onSubmit={onSubmit}
      defaultValue={defaultValue}
      validation={validation}
      getValueFromEvent={getValueFromEvent}
      getChangedForm={getChangedForm}
    >
      <Paper>
        <Box padding={4}>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Box
                paddingY={1.25}
                paddingX={2}
                className={classes.pinkTitleBar}
              >
                <Typography variant="h5">
                  {title}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <FormField<EditFinanceDepositWayGroupFormType, TextFieldProps>
                context={FormContext}
                component={TextField}
                name="name"
                label={t('common.depositWayGroupName')}
                required
              />
            </Grid>
            <Grid item>
              <Typography className={classes.bold}>{t('common.status')}</Typography>
              <FormField<EditFinanceDepositWayGroupFormType, OnOffCheckboxProps>
                component={OnOffCheckbox}
                context={FormContext}
                name="isActive"
                label={t('common.toggleOn')}
              />
            </Grid>
          </Grid>
          <Grid item>
            <Box
              marginTop={3}
              marginBottom={3}
              borderBottom={`4px solid ${theme.palette.grey[200]}`}
            />
          </Grid>
          <SearchMethodsForm
            context={searchContext}
            groupId={groupId}
            pageFlow={pageFlow}
          />
        </Box>
        <SearchResTable
          context={searchContext}
          groupId={groupId}
        />
      </Paper>
      <Box paddingY={3} display="flex" flexDirection="row" justifyContent="flex-end">
        <Box paddingX={2}>
          <Button
            className={classes.greyButton}
            onClick={onBack}
          >
            {t('common.cancel')}
          </Button>
        </Box>
        <FormSubmitButton
          context={FormContext}
          component={Button}
          type="submit"
          className={classes.purpleGradualButton}
        >
          {okText}
        </FormSubmitButton>
      </Box>
    </FormStateProvider>
  )
}

export default React.memo(EditFinanceDepositWayGroupForm)
